import React, { useState, useEffect } from 'react';
import { svgCertificateGenerator } from '../../lib/svgCertificateGenerator';
import { CertificateElementEditor } from './CertificateElementEditor';
import { CertificatePreviewPanel } from './CertificatePreviewPanel';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Escape HTML metacharacters so text values cannot inject markup/script when
// written into a same-origin popup via document.write (DOM-XSS hardening).
// Order matters: '&' must be replaced first.
const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

interface TestCertificateData {
  userName: string;
  userIdNumber: string;
  addressLine1: string;
  addressLine2: string;
  sharesCount: number;
  certificateNumber: string;
  issueDate: string;
  sunId: string;
}

interface CertificateTemplateElement {
  id: string;
  element_id: string;
  element_type: 'text' | 'image' | 'line' | 'rectangle' | 'watermark';
  template_version: string;
  x_position: number;
  y_position: number;
  width?: number;
  height?: number;
  font_family?: string;
  font_size?: number;
  font_weight?: string;
  font_color?: string;
  text_anchor?: string;
  static_content?: string;
  data_field?: string;
  is_dynamic: boolean;
  opacity?: number;
  rotation?: number;
  z_index?: number;
  description?: string;
  is_active: boolean;
}

export const CertificateTestSystem: React.FC = () => {
  const [testData, setTestData] = useState<TestCertificateData>({
    userName: 'Francois Johannes Prins',
    userIdNumber: '6803265048083',
    addressLine1: '1St Thomas close, Anchorage Park, Gordonsbay, South Africa, 7140',
    addressLine2: '',
    sharesCount: 700,
    certificateNumber: '0-000-001',
    issueDate: '27/06/2025',
    sunId: '40681'
  });

  const [previewSvg, setPreviewSvg] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [selectedElement, setSelectedElement] = useState<CertificateTemplateElement | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info' | null>(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateUploadStatus, setTemplateUploadStatus] = useState<string | null>(null);

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      // Force reload the template
      await svgCertificateGenerator.loadTemplate();
      generatePreview();
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = async () => {
    try {
      setLoading(true);

      // Use the SVG certificate generator to create test certificate
      const testCertificate = await svgCertificateGenerator.generateTestCertificate({
        userName: testData.userName,
        userIdNumber: testData.userIdNumber,
        addressLine1: testData.addressLine1,
        addressLine2: testData.addressLine2,
        sharesCount: testData.sharesCount,
        certificateNumber: testData.certificateNumber,
        sunId: testData.sunId,
        issueDate: testData.issueDate
      });

      if (testCertificate) {
        setPreviewSvg(testCertificate);
      } else {
        console.warn('No certificate generated - using fallback');
        // Generate a simple fallback preview
        setPreviewSvg(`
          <svg width="800" height="600" xmlns="http://www.w3.org/2000/svg">
            <rect width="800" height="600" fill="#f8f9fa" stroke="#dee2e6" stroke-width="2"/>
            <text x="400" y="300" text-anchor="middle" font-family="Arial" font-size="24" fill="#6c757d">
              Certificate Preview Unavailable
            </text>
            <text x="400" y="340" text-anchor="middle" font-family="Arial" font-size="16" fill="#6c757d">
              Check network connection and try again
            </text>
          </svg>
        `);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      // Show error state in preview instead of alert
      setPreviewSvg(`
        <svg width="800" height="600" xmlns="http://www.w3.org/2000/svg">
          <rect width="800" height="600" fill="#fff5f5" stroke="#fed7d7" stroke-width="2"/>
          <text x="400" y="280" text-anchor="middle" font-family="Arial" font-size="20" fill="#e53e3e">
            ❌ Error Generating Certificate
          </text>
          <text x="400" y="320" text-anchor="middle" font-family="Arial" font-size="14" fill="#c53030">
            ${error instanceof Error ? error.message : 'Unknown error occurred'}
          </text>
          <text x="400" y="360" text-anchor="middle" font-family="Arial" font-size="12" fill="#9c4221">
            Check console for details
          </text>
        </svg>
      `);
    } finally {
      setLoading(false);
    }
  };

  const downloadTestPNG = async () => {
    try {
      setLoading(true);
      setStatusMessage(null);
      setStatusType(null);

      if (!previewSvg) {
        await generatePreview();
        return;
      }

      // Convert SVG to high-quality PNG
      const pngBlob = await svgCertificateGenerator.svgToHighQualityPNG(previewSvg);

      if (pngBlob) {
        const url = URL.createObjectURL(pngBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `test-certificate-${testData.certificateNumber}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setStatusType('success');
        setStatusMessage(`Test certificate PNG downloaded! Size: ${(pngBlob.size / 1024 / 1024).toFixed(2)}MB`);
      } else {
        setStatusType('error');
        setStatusMessage('Failed to generate PNG');
      }
    } catch (error) {
      console.error('Error downloading PNG:', error);
      setStatusType('error');
      setStatusMessage('Error downloading PNG: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const printTestCertificate = () => {
    setStatusMessage(null);
    setStatusType(null);

    if (!previewSvg) {
      setStatusType('error');
      setStatusMessage('Please generate preview first');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Test Certificate Print - ${testData.certificateNumber}</title>
            <style>
              body { 
                margin: 0; 
                padding: 20px; 
                background: white;
                font-family: Arial, sans-serif;
              }
              .container { 
                max-width: 1000px; 
                margin: 0 auto; 
                text-align: center;
              }
              .header {
                margin-bottom: 20px;
                padding-bottom: 10px;
                border-bottom: 2px solid #D4AF37;
              }
              .certificate-preview {
                border: 2px solid #D4AF37;
                padding: 10px;
                background: white;
              }
              @media print {
                body { padding: 0; }
                .header { display: none; }
                .certificate-preview { border: none; padding: 0; }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1>🧪 TEST CERTIFICATE</h1>
                <p><strong>Certificate Number:</strong> ${escapeHtml(testData.certificateNumber)}</p>
                <p><strong>User:</strong> ${escapeHtml(testData.userName)}</p>
                <p><strong>Shares:</strong> ${escapeHtml(testData.sharesCount)}</p>
              </div>
              <div class="certificate-preview">
                ${previewSvg}
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const resetToDefaults = () => {
    setTestData({
      userName: 'Francois Johannes Prins',
      userIdNumber: '6803265048083',
      addressLine1: '1St Thomas close, Anchorage Park, Gordonsbay, South Africa, 7140',
      addressLine2: '',
      sharesCount: 700,
      certificateNumber: '0-000-001',
      issueDate: '27/06/2025',
      sunId: '40681'
    });
  };

  const handleInputChange = (field: keyof TestCertificateData, value: string | number) => {
    setTestData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleElementUpdate = (element: CertificateTemplateElement) => {
    setSelectedElement(element);
    // Trigger preview refresh
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTemplateUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('svg')) {
      setTemplateUploadStatus('❌ Please upload an SVG file');
      setTimeout(() => setTemplateUploadStatus(null), 3000);
      return;
    }

    try {
      setUploadingTemplate(true);
      setTemplateUploadStatus('📤 Uploading template...');

      // Read file as text to validate SVG content
      const fileText = await file.text();
      if (!fileText.includes('<svg') || !fileText.includes('</svg>')) {
        setTemplateUploadStatus('❌ Invalid SVG file');
        setTimeout(() => setTemplateUploadStatus(null), 3000);
        return;
      }

      // Upload template via API
      const response = await fetch(`${API_BASE_URL}/api/admin/certificates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'upload_template',
          data: { svgContent: fileText, fileName: 'template.svg' }
        })
      });
      const result = await response.json();

      if (!response.ok) {
        console.error('Template upload error:', result.error);
        setTemplateUploadStatus(`❌ Upload failed: ${result.error}`);
        setTimeout(() => setTemplateUploadStatus(null), 5000);
        return;
      }

	      const publicUrl = result?.data?.publicUrl || result?.publicUrl;
	      if (publicUrl) {
	        localStorage.setItem('aureusCertificateCustomTemplateEnabled', 'true');
	        localStorage.setItem('aureusCertificateCustomTemplateUrl', publicUrl);
	      } else {
	        localStorage.removeItem('aureusCertificateCustomTemplateEnabled');
	        localStorage.removeItem('aureusCertificateCustomTemplateUrl');
	      }

      setTemplateUploadStatus('✅ Template uploaded successfully! Reloading...');

      // Reload the template and regenerate preview
      await loadTemplate();

      setTemplateUploadStatus('✅ Template updated and preview refreshed!');
      setTimeout(() => setTemplateUploadStatus(null), 3000);

    } catch (error) {
      console.error('Error uploading template:', error);
      setTemplateUploadStatus(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setTemplateUploadStatus(null), 5000);
    } finally {
      setUploadingTemplate(false);
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">🧪 Certificate Test System</h1>
          <p className="text-white">Test and perfect the certificate design before creating real certificates</p>
        </div>

        {/* Status banner */}
        {statusMessage && (
          <div
            className={`mb-6 p-3 rounded border text-sm ${
              statusType === 'success'
                ? 'bg-green-900/40 border-green-500 text-green-200'
                : statusType === 'error'
                ? 'bg-red-900/40 border-red-500 text-red-200'
                : 'bg-blue-900/40 border-blue-500 text-blue-200'
            }`}
          >
            {statusMessage}
          </div>
        )}

        <div className="space-y-8">
          {/* Template Upload Section */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4">🎨 Certificate Template Management</h2>
            <p className="text-sm text-white mb-4">
              Upload a new SVG template to customize the certificate design. The template will be used for all future certificate generations.
            </p>

            <div className="flex items-center gap-4">
              <label className="flex-1">
                <input
                  type="file"
                  accept=".svg,image/svg+xml"
                  onChange={handleTemplateUpload}
                  disabled={uploadingTemplate}
                  className="hidden"
                  id="template-upload"
                />
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="template-upload"
                    className={`px-4 py-2 rounded cursor-pointer transition-colors ${
                      uploadingTemplate
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {uploadingTemplate ? '⏳ Uploading...' : '📤 Upload New Template (SVG)'}
                  </label>
                </div>
              </label>

              <button
                onClick={loadTemplate}
                disabled={loading || uploadingTemplate}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔄 Reload Template
              </button>
            </div>

            {templateUploadStatus && (
              <div className={`mt-3 p-3 rounded border text-sm ${
                templateUploadStatus.includes('✅')
                  ? 'bg-green-900/40 border-green-500 text-green-200'
                  : templateUploadStatus.includes('❌')
                  ? 'bg-red-900/40 border-red-500 text-red-200'
                  : 'bg-blue-900/40 border-blue-500 text-blue-200'
              }`}>
                {templateUploadStatus}
              </div>
            )}

            <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
              <p className="text-sm text-white">
                <strong className="text-gray-300">Default Template:</strong> /certificates/certificate-template.svg<br />
                <strong className="text-gray-300">Default PNG Background:</strong> /certificates/certificate_background.png<br />
                <strong className="text-gray-300">Custom Upload:</strong> certificates/template.svg<br />
                <strong className="text-gray-300">Note:</strong> All certificates will be regenerated from the template with dynamic text overlays.
              </p>
            </div>
          </div>

          {/* Element Positioning Controls */}
          <CertificateElementEditor
            onElementUpdate={handleElementUpdate}
          />

          <div className="grid lg:grid-cols-2 gap-8">
          {/* Test Data Form */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4">📝 Test Certificate Data</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">User Name</label>
                <input
                  type="text"
                  value={testData.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">ID Number</label>
                <input
                  type="text"
                  value={testData.userIdNumber}
                  onChange={(e) => handleInputChange('userIdNumber', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={testData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Address Line 2</label>
                <input
                  type="text"
                  value={testData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Shares Count</label>
                  <input
                    type="number"
                    value={testData.sharesCount}
                    onChange={(e) => {
                      const parsed = Number.parseInt(e.target.value, 10);
                      handleInputChange('sharesCount', Number.isFinite(parsed) ? parsed : 0);
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">SUN ID</label>
                  <input
                    type="text"
                    value={testData.sunId}
                    onChange={(e) => handleInputChange('sunId', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Certificate Number</label>
                <input
                  type="text"
                  value={testData.certificateNumber}
                  onChange={(e) => handleInputChange('certificateNumber', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Issue Date</label>
                <input
                  type="text"
                  value={testData.issueDate}
                  onChange={(e) => handleInputChange('issueDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            {/* QR Code Information */}
            <div className="mt-6 p-4 bg-gray-800 rounded border border-yellow-600">
              <h3 className="text-sm font-semibold text-yellow-400 mb-2">📱 QR Code Information</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-white">QR Code URL:</span>
                  <div className="mt-1 p-2 bg-gray-800 rounded font-mono text-green-400 break-all">
                    {`https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/certificates/certificate-${testData.certificateNumber}-user-${testData.sunId}.png`}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-white">
                  <span>✓ Color: Gold (#c39a3f)</span>
                  <span>✓ Background: White</span>
                  <span>✓ Position: (605, 355)</span>
                  <span>✓ Size: 225px</span>
                </div>
                <div className="text-white">
                  <strong>Note:</strong> QR code is embedded in the bottom-right corner with white background for better visibility and scanning (50% bigger)
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={generatePreview}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? '⏳ Generating...' : '🔄 Generate Preview'}
              </button>

              <button
                onClick={downloadTestPNG}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                disabled={loading || !previewSvg}
              >
                📥 Download PNG
              </button>

              <button
                onClick={printTestCertificate}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                disabled={!previewSvg}
              >
                🖨️ Print Test
              </button>

              <button
                onClick={resetToDefaults}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                🔄 Reset Defaults
              </button>
            </div>
          </div>

          {/* Enhanced Preview Panel */}
          <CertificatePreviewPanel
            testData={testData as any}
            selectedElement={selectedElement}
            onElementUpdate={handleElementUpdate}
            refreshTrigger={refreshTrigger}
          />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTestSystem;
